import { useRouter } from 'next/router';
import useChallenges from 'lib/hooks/useChallenges';

import {
  Bubble,
  Card,
  Difficulty,
  flash,
  Gallery,
  Heading,
  Icon,
  Languages,
  LinkWithButtonStyles,
} from '@frontendmentorio/design-system-v2';

import CTA from './CTA';
import Flag from 'features/shared/Flag';

export default function Hero({ challenge, inProgress }) {
  const { unlock } = useChallenges();
  const router = useRouter();

  const handleStart = async () => {
    try {
      await unlock(challenge);

      flash.success('Challenge started. Happy coding!');
      router.push(`/challenges/${challenge.slug}?tab=downloads`);
    } catch (error) {
      flash.error(error.message);
    }
  };

  return (
    <Card
      className="flex flex-col-reverse gap-5 p-5 tablet:flex-row tablet:gap-6 tablet:items-center"
      data-testid="hero"
    >
      <div className="flex flex-col gap-5 items-start tablet:w-1/2">
        <div className="flex flex-wrap gap-4 items-center justify-between w-full">
          <div className="flex gap-4 items-center">
            <Flag type={challenge.type} />
            <Languages languages={challenge.languages} />
          </div>

          <Difficulty level={challenge.difficulty} />
        </div>

        <div className="flex flex-col gap-5">
          <Heading level="4" tagName="h1">
            {challenge.title}
          </Heading>

          <p className="text-neutral-500">{challenge.description}</p>

          {inProgress && (
            <div className="bg-gray-100 flex gap-3 items-center p-5 rounded-xl">
              <Icon
                className="text-blue-500 text-xl"
                name="circle-exclamation"
              />

              <span className="font-secondary font-semibold">
                It looks like you've already started this challenge
              </span>
            </div>
          )}

          {inProgress ? (
            <LinkWithButtonStyles
              className="gap-2"
              href={`/challenges/${challenge.slug}?tab=submit`}
              isWide
              level="secondary"
            >
              Submit solution <Icon name="right-to-line" style="solid" />
            </LinkWithButtonStyles>
          ) : (
            <CTA
              challenge={challenge}
              onStart={handleStart}
              proLockMessage="This is a premium challenge. You’ll need a Pro subscription to start this challenge."
              withCTA={false}
            />
          )}

          {!inProgress && (
            <div className="flex gap-4 items-center">
              <Bubble className="bg-blue-100">
                <Icon className="text-xl text-blue-500" name="users" />
              </Bubble>

              <p>
                Join{' '}
                <span className="font-bold">
                  {challenge.startedCount.toLocaleString()}
                </span>{' '}
                people who have taken this challenge
              </p>
            </div>
          )}
        </div>
      </div>

      <Gallery
        className="items-center tablet:w-1/2"
        images={challenge.gallery}
        title={`Image gallery for the ${challenge.title} challenge`}
      />
    </Card>
  );
}
